import React from 'react'
import { CSVLink } from "react-csv";
import {
    DataTable,
    Table,
    Button,
    Pagination,
    TableBatchAction,
    TableBatchActions,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    TableSelectAll,
    TableSelectRow,
    TableToolbar,
    TableToolbarAction,
    TableToolbarContent,
    TableToolbarSearch,
    TableToolbarMenu,
    Modal,
    NotificationActionButton,
    InlineNotification,
    OverflowMenu,
    OverflowMenuItem,
    Form,
    TextInput
} from 'carbon-components-react';
import {
    Delete16 as Delete,
    Save16 as Save,
    Download16 as Download,
    Edit16 as Edit
} from '@carbon/icons-react';

const currenciesHeaders = [
    { header: "ID", key: "id" },
    { header: "Tax Name", key: "name" },
    { header: "Rate", key: "rate" },
    { header: "Action", key: "action" },
];

export default {
    title: 'Components/DataTable/Batch Actions',
    component: DataTable,
    subcomponents: {
        TableBatchAction,
        TableBatchActions,
        TableToolbar,
        TableToolbarAction,
        TableToolbarContent,
        TableToolbarSearch,
        TableToolbarMenu,
        Table,
        TableBody,
        TableCell,
        TableContainer,
        TableHead,
        TableHeader,
        TableRow,
    },
};

export const TaxRatesList = ({ setDeleteID, csvTaxRateReport, setTaxRateSearch, deleteID, batchDelete, openDeleteModal, setOpenDeleteModal, editID, editSetup, editForm, openEditModal, setOpenEditModal, editCurrency, addCurrency, addForm, openAddModal, setOpenAddModal, error, setError, rows, totalItems, currentPageSize, setCurrentPageSize, setFirstRowIndex }: any) => (
    <>
        <DataTable rows={rows} headers={currenciesHeaders} isSortable>
            {({
                rows,
                headers,
                getHeaderProps,
                getRowProps,
                getSelectionProps,
                getToolbarProps,
                getBatchActionProps,
                onInputChange,
                selectedRows,
                getTableProps,
                getTableContainerProps,
            }) => {
                const batchActionProps = getBatchActionProps();
                return (
                    <TableContainer
                        {...getTableContainerProps()}>
                        <TableToolbar {...getToolbarProps()}>
                            <TableBatchActions {...batchActionProps}>
                                <TableBatchAction
                                    tabIndex={batchActionProps.shouldShowBatchActions ? 0 : -1}
                                    renderIcon={Delete}
                                    onClick={() => setOpenDeleteModal(true) & setDeleteID(selectedRows)}
                                >
                                    Delete
                                </TableBatchAction>
                            </TableBatchActions>
                            <TableToolbarContent
                                aria-hidden={batchActionProps.shouldShowBatchActions}>
                                <TableToolbarSearch
                                    persistent={true}
                                    tabIndex={batchActionProps.shouldShowBatchActions ? -1 : 0}
                                    onChange={(e) => setTaxRateSearch(e.target.value)}
                                    placeholder="Search tax rates"
                                />
                                <TableToolbarMenu
                                    tabIndex={batchActionProps.shouldShowBatchActions ? -1 : 0}>
                                    <TableToolbarAction>
                                        <CSVLink className="text-dark dec-none" {...csvTaxRateReport}>Export to CSV</CSVLink>
                                    </TableToolbarAction>
                                </TableToolbarMenu>
                                <Button
                                    tabIndex={batchActionProps.shouldShowBatchActions ? -1 : 0}
                                    size="small"
                                    kind="primary"
                                    onClick={() => setOpenAddModal(true)}
                                >
                                    Add new
                                </Button>
                            </TableToolbarContent>
                        </TableToolbar>
                        <Table {...getTableProps()}>
                            <TableHead>
                                <TableRow>
                                    <TableSelectAll {...getSelectionProps()} />
                                    {headers.map((header, i) => (
                                        <TableHeader key={i} {...getHeaderProps({ header })}>
                                            {header.header}
                                        </TableHeader>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row, i) => (
                                    <TableRow key={i} {...getRowProps({ row })}>
                                        <TableSelectRow {...getSelectionProps({ row })} />
                                        {row.cells.map((cell) => (
                                            <TableCell key={cell.id}>
                                                {cell.info.header === "action" ? <OverflowMenu renderIcon={Edit} className="m-auto">
                                                    <OverflowMenuItem onClick={()=>editSetup(row.id) & setOpenEditModal(true)} itemText="Edit" />
                                                    <OverflowMenuItem onClick={()=>setDeleteID([row]) & setOpenDeleteModal(true)} hasDivider isDelete itemText="Delete" />
                                                </OverflowMenu> : cell.info.header === "rate" ? cell.value + '%' : cell.value}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Pagination
                            totalItems={totalItems}
                            pageSizes={[10, 20, 30, 40, 50]}
                            pageSize={currentPageSize}
                            onChange={({ page, pageSize }) => {
                                if (pageSize !== currentPageSize) {
                                    setCurrentPageSize(pageSize);
                                }
                                setFirstRowIndex(pageSize * (page - 1));
                            }}
                        />
                    </TableContainer>
                );
            }}
        </DataTable>

        <Modal
            open={openAddModal}
            modalHeading="Add tax rate"
            modalLabel="System Tax Rates"
            primaryButtonText="Add"
            secondaryButtonText="Cancel"
            hasForm={true}
            onRequestClose={() => setOpenAddModal(false) & setError(false)}
            onRequestSubmit={() => addForm.current && addForm.current.click() & setError(false)}
        >   {error && <InlineNotification kind="error" hideCloseButton={true} title="Error: Unable to add new system tax rate" subtitle="Please try again or contact your administrator." actions={<NotificationActionButton onClick={(() => setError(false))}>Dismiss</NotificationActionButton>} />}
            <p style={{ marginBottom: '1rem' }}>
                Fill out the form to add a new tax rate that can be used within the system.
            </p>
            <Form id="addCurrency" onSubmit={(values) => addCurrency(values)} className="d-flex flex-w">
                <div className="col-12 col-md-6 m-005">
                    <TextInput
                        data-modal-primary-focus
                        id="name"
                        name="name"
                        labelText="Tax Rate Name"
                        placeholder="Enter tax rate name"
                        invalidText="Tax Rate Name is required"
                        style={{ marginBottom: '1rem' }}
                        required
                    />
                </div>
                <div className="col-12 col-md-6 m-005">
                    <TextInput
                        data-modal-primary-focus
                        id="rate"
                        name="rate"
                        labelText="Tax Rate"
                        placeholder="Enter tax rate"
                        invalidText="Tax Rate is required"
                        style={{ marginBottom: '1rem' }}
                        required
                    />
                </div>
                <input type="submit" style={{ display: 'none' }} ref={addForm} />
            </Form>
        </Modal>

        <Modal
            open={openEditModal}
            modalHeading="Edit currency"
            modalLabel="System Currencies"
            primaryButtonText="Save Changes"
            secondaryButtonText="Cancel"
            hasForm={true}
            onRequestClose={() => setOpenEditModal(false) & setError(false)}
            onRequestSubmit={() => editForm.current && editForm.current.click() & setError(false)}
        >   {error && <InlineNotification kind="error" hideCloseButton={true} title="Error: Unable to edit system currency" subtitle="Please try again or contact your administrator." actions={<NotificationActionButton onClick={(() => setError(false))}>Dismiss</NotificationActionButton>} />}
            <p style={{ marginBottom: '1rem' }}>
                Fill out the form to edit the selected currency.
            </p>
            <Form id="editCurrency" onSubmit={(values) => editCurrency(values)} className="d-flex flex-w">
                <div className="col-12 col-md-6 m-005">
                    <TextInput
                        data-modal-primary-focus
                        id="currency"
                        name="currency"
                        labelText="Currency Description"
                        placeholder="Enter description"
                        invalidText="Currency Description is required"
                        style={{ marginBottom: '1rem' }}
                        defaultValue={editID?.currency}
                        required
                    />
                </div>
                <div className="col-12 col-md-6 m-005">
                    <TextInput
                        data-modal-primary-focus
                        id="currencycode"
                        name="currencycode"
                        labelText="Currency Code"
                        placeholder="Enter currency code"
                        invalidText="Currency Code is required"
                        style={{ marginBottom: '1rem' }}
                        defaultValue={editID?.currencycode}
                        required
                    />
                </div>
                <input type="submit" style={{ display: 'none' }} ref={editForm} />
            </Form>
        </Modal>

        <Modal
            danger
            open={openDeleteModal}
            modalHeading="Delete System Currency"
            modalLabel="System Currencies"
            primaryButtonText="Delete"
            secondaryButtonText="Cancel"
            onRequestClose={() => setOpenDeleteModal(false) & setError(false)}
            onRequestSubmit={() => batchDelete(deleteID)}
        >
            {error && <InlineNotification kind="error" hideCloseButton={true} title="Error: Unable to delete system currency" subtitle="Please try again or contact your administrator." actions={<NotificationActionButton onClick={(() => setError(false))}>Dismiss</NotificationActionButton>} />}
            <p>Are you sure you want to delete the selected system currencies?</p>

        </Modal>
    </>
);